import { IMainComponent } from 'src/generated-types/main-component'
import { MainComponentProgress } from '../../locality-dashboard/cage-status/MainComponentProgress'
import { ICertificate } from 'src/generated-types/Certificate'
import { CertificationValidationPeriod, IMainComponentType, MainComponentStatus } from 'src/enums'
import { IMainComponentStatus } from 'src/generated-types/main-component-status'
import { useMemo } from 'react'
import { sortCertificates } from 'src/utils/function'
import { setToMidnight } from 'src/utils'
import { differenceInDays } from 'date-fns'

interface EquipmentModalProgressBarProps {
  mainComponent: IMainComponent
  certificates: ICertificate[]
  showExtraDetails?: boolean
}
const EquipmentModalProgressBar = ({
  mainComponent,
  certificates,
  showExtraDetails,
}: EquipmentModalProgressBarProps) => {
  const sortedCertificates = useMemo(() => {
    const sorted = sortCertificates(certificates || [])

    return sorted.map((cert, index) => ({
      ...cert,
      isCurrentlyActive:
        index === 0 &&
        cert.isValid === true &&
        (() => {
          const validUntil = cert?.validUntil?.value ?? cert?.validUntil
          const expiryDate = validUntil ? new Date(validUntil).getTime() : Number.MAX_SAFE_INTEGER
          const now = setToMidnight(new Date()).getTime()
          return expiryDate >= now
        })(),
    }))
  }, [certificates])

  const certificate = sortedCertificates.find(cert => cert.isCurrentlyActive)

  const certificateValidationTime = useMemo(() => {
    if (certificate?.expiresIn == null || certificate?.numberOfDays == null) return null
    if (mainComponent.status === MainComponentStatus.Active && mainComponent.expiryDate && certificate?.numberOfDays) {
      const expiryDate = mainComponent.expiryDate
      const daysRemaining = differenceInDays(new Date(expiryDate), new Date())
      return Math.min((daysRemaining * 100) / certificate?.numberOfDays, 100)
    }
    if (certificate?.expiresIn && certificate?.numberOfDays) {
      return Math.min((certificate?.expiresIn * 100) / certificate?.numberOfDays, 100)
    }
    return 0
  }, [certificate, mainComponent])

  const status: CertificationValidationPeriod = useMemo(() => {
    if (certificateValidationTime != undefined && certificateValidationTime >= 0) {
      if (certificateValidationTime < CertificationValidationPeriod.LOW) {
        return CertificationValidationPeriod.LOW
      } else if (certificateValidationTime < CertificationValidationPeriod.MEDIUM) {
        return CertificationValidationPeriod.MEDIUM
      } else if (certificateValidationTime <= CertificationValidationPeriod.HIGH) {
        return CertificationValidationPeriod.HIGH
      }
    }
    return CertificationValidationPeriod.NA
  }, [certificateValidationTime])
  return (
    <MainComponentProgress
      minWidth={'300px'}
      fontSize={'14px'}
      progressBarHeight={'6.85px'}
      cage={
        {
          nets: mainComponent?.type.componentType === IMainComponentType.Net ? [mainComponent] : [],
          rings: mainComponent?.type.componentType === IMainComponentType.Ring ? [mainComponent] : [],
          daysLeft: mainComponent.expires,
          certificateValidationTime: certificateValidationTime,
        } as IMainComponentStatus
      }
      status={status}
      selectedMainComponent={mainComponent as IMainComponent}
      openEquipmentModal={false}
      fontWeight={400}
      showExtraDetails={showExtraDetails}
    />
  )
}

export default EquipmentModalProgressBar
